import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { fetchPaymentStatus } from '../lib/api';
import { usePageMeta } from '../hooks/use-page-meta';

const serif = { fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 };
const MAX_POLLS = 8;

const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-6" style={{ paddingTop: '100px', paddingBottom: '60px' }} data-testid={testId}>
    <div className="w-full max-w-md text-center p-10 border border-white/10 bg-[#242424] fade-in-up">{children}</div>
  </div>
);

export const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('payment_intent_id') || params.get('session_id');
  const linkId = params.get('link');
  const [state, setState] = useState('checking');
  usePageMeta({ title: 'Payment', description: 'Payment confirmation for your session with Elina K. Photography.', path: '/payment/success' });

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { payment_status } = await fetchPaymentStatus(sessionId);
        if (payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired'].includes(payment_status)) { setState('failed'); return; }
      } catch (e) { /* keep polling */ }
      if (attempts < MAX_POLLS) timer = setTimeout(poll, 2000);
      else setState('pending');
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state === 'paid') return (
    <Shell testId="payment-success">
      <CheckCircle className="w-14 h-14 text-[#c9a87c] mx-auto mb-6" />
      <h1 className="text-3xl mb-4" style={serif}>Thank you!</h1>
      <p className="text-[#b8b8b8]">Your payment was received. Elina will be in touch shortly with the next steps for your session.</p>
      <Link to="/" className="hero-cta inline-block mt-8" data-testid="payment-home-btn">Back to Home</Link>
    </Shell>
  );

  if (state === 'failed' || state === 'error') return (
    <Shell testId="payment-failed">
      <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
      <h1 className="text-3xl mb-4" style={serif}>Payment not completed</h1>
      <p className="text-[#b8b8b8]">Something went wrong with the payment.</p>
      {linkId && <Link to={`/pay/${linkId}`} className="hero-cta inline-block mt-8" data-testid="payment-retry-btn">Try again</Link>}
    </Shell>
  );

  return (
    <Shell testId={state === 'pending' ? 'payment-pending' : 'payment-checking'}>
      <Clock className="w-14 h-14 text-[#c9a87c] mx-auto mb-6 animate-pulse" />
      <h1 className="text-3xl mb-4" style={serif}>{state === 'pending' ? 'Payment is being confirmed' : 'Confirming your payment…'}</h1>
      <p className="text-[#b8b8b8]">{state === 'pending' ? 'Your bank is still processing it. You will receive a confirmation shortly.' : 'This only takes a moment.'}</p>
    </Shell>
  );
};
